import { Outlet, createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';

import { NotFound } from '@/components/Error/NotFound';
import { ProductLogoMediumAttendanceCore as LogoAttendanceCore } from '@/components/Logo';
import type { TabWithIndicatorProps } from '@/components/Navigation/HeaderTabs';
import { ProductHeader } from '@/components/ProductHeader';
import { OperationEventConfigProvider } from '@/features/operation-event/OperationEventConfigProvider';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { featureFlags } from '@/lib/env';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { getAttendanceGanttDashboard } from './_layout/dashboard/-api';
import { getAttendanceCoreOperationPackages } from './_layout/operation-packages/-api';

export const Route = createFileRoute('/_staff/attendance-core/_layout')({
  component: Layout,
});

function Layout() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.attendanceCore',
  });
  useDocumentTitle(t('pageTitle'));

  const tabs: TabWithIndicatorProps[] = useMemo(
    () => [
      {
        to: '/attendance-core/dashboard',
        children: t('tabs.dashboard'),
        activeOptions: { exact: true },
      },
      {
        to: '/attendance-core/operation-packages',
        children: t('tabs.operationPackages'),
        activeOptions: { exact: false },
      },
      {
        to: '/attendance-core/archive',
        children: t('tabs.archive'),
        activeOptions: { exact: true },
      },
      {
        to: '/attendance-core/config',
        children: t('tabs.config'),
        activeOptions: { exact: true },
      },
    ],
    [t],
  );

  if (!featureFlags.check('LABOR_PLUS_MOCK')) {
    return <NotFound />;
  }

  return (
    <OperationEventConfigProvider
      value={{
        pagesRootKey: 'attendanceCore',
        ganttDashboardQueryKey: ['attendance-core', 'dashboard', 'gantt'],
        getGanttDashboard: getAttendanceGanttDashboard,
        operationPackagesQueryKey: ['attendance-core', 'operation-packages'],
        getOperationPackages: getAttendanceCoreOperationPackages,
      }}
    >
      <div className="flex h-full min-h-0 flex-1 flex-col">
        <ProductHeader
          Logo={LogoAttendanceCore}
          name={t('pageTitle')}
          tabs={tabs}
          type="core"
        />
        <div className="flex min-h-0 flex-1 flex-col">
          <Outlet />
        </div>
      </div>
    </OperationEventConfigProvider>
  );
}
